/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import linkEditorCss from './link-editor.css?inline';
import { $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  GridSelection,
  KEY_ESCAPE_COMMAND,
  LexicalEditor,
  NodeSelection,
  RangeSelection,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { Dispatch, useCallback, useEffect, useRef, useState, type JSX } from 'react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  HStack,
  Text,
  Input,
  Link,
  Stack,
  Flex,
  PopoverCloseButton,
  IconButton,
  usePrevious,
} from '@chakra-ui/react';
import { RiCloseFill } from 'react-icons/ri';
import { getSelectedNode, sanitizeUrl, setFloatingElemPositionForLinkEditor } from './utils';

function CssInjector() {
  const [isMounted, setIsMounted] = useState(typeof window !== 'undefined');

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <style
      key="link-editor-styles"
      dangerouslySetInnerHTML={{
        __html: `${linkEditorCss}`,
      }}
    />
  );
}

function FloatingLinkEditor({
  editor,
  isLink,
  setIsLink,
  anchorElem,
  isLinkEditMode,
  setIsLinkEditMode,
}: {
  editor: LexicalEditor;
  isLink: boolean;
  setIsLink: Dispatch<boolean>;
  anchorElem: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
}): JSX.Element {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [editedLinkUrl, setEditedLinkUrl] = useState('');
  const [lastSelection, setLastSelection] = useState<
    RangeSelection | GridSelection | NodeSelection | null
  >(null);

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);

      const parent = node.getParent();
      if ($isLinkNode(parent)) {
        const u = parent.getURL();
        setLinkUrl(u);
        if (!u) setIsLinkEditMode(true);
      } else if ($isLinkNode(node)) {
        const u = node.getURL();
        setLinkUrl(u);
        if (!u) setIsLinkEditMode(true);
      } else {
        setLinkUrl('');
      }
    }
    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      selection !== null &&
      nativeSelection !== null &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editor.isEditable()
    ) {
      const domRect: DOMRect | undefined =
        nativeSelection.focusNode?.parentElement?.getBoundingClientRect();
      if (domRect) {
        domRect.y += 40;
        setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== 'link-input') {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem, anchorElem);
      }
      setLastSelection(null);
      setIsLinkEditMode(false);
      setLinkUrl('');
    }

    return true;
  }, [anchorElem, editor, setIsLinkEditMode]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        updateLinkEditor();
      });
    };

    window.addEventListener('resize', update);

    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);

      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [anchorElem.parentElement, editor, updateLinkEditor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink) {
            setIsLink(false);
            if (editedLinkUrl === '') {
              editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
            }
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor, updateLinkEditor, setIsLink, editedLinkUrl, isLink]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    if (isLinkEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLinkEditMode, isLink]);

  const monitorInputInteraction = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleLinkSubmission();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsLinkEditMode(false);
      if (editedLinkUrl === '') {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }
    }
  };

  const handleLinkSubmission = () => {
    if (lastSelection !== null) {
      if (editedLinkUrl !== '') {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
          url: sanitizeUrl(editedLinkUrl),
          target: '_blank',
          rel: 'noopener noreferrer',
        });
        setIsLinkEditMode(false);
      } else {
        setEditedLinkUrl('');
        setIsLinkEditMode(false);
      }
    }
  };

  // const prevIsEditMode = usePrevious(isLinkEditMode);

  // useEffect(() => {
  //   if (prevIsEditMode && !isLinkEditMode && editedLinkUrl === '') {
  //     editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
  //   }
  // }, [isLinkEditMode]);

  const popoverStyles = {
    bgColor: 'brand.secondary',
    padding: 3, // change the padding of the content
    border: '1px dashed',
    borderColor: 'black',
    textStyle: 'brand.body',
    borderRadius: '0px',
    marginTop: '12px',
    // zIndex: 100000,
  };

  const popoverClassname =
    'flex flex-col gap-[12px] bg-brand-background border-[1px] border-brand-main border-dashed mt-[12px] p-[6px]';

  return (
    <>
      <CssInjector />
      <div ref={editorRef} className="link-editor">
        {!isLink ? null : isLinkEditMode ? (
          <div className={popoverClassname}>
            <div className="flex justify-between items-center">
              <p className="typography-action-sm">Link</p>
              <IconButton
                variant="icon"
                icon={<RiCloseFill />}
                color="brand.main"
                height="fit-content"
                aria-label="Format text as italics"
                onClick={() => {
                  setIsLink(false);
                  setIsLinkEditMode(false);
                  if (editedLinkUrl === '') {
                    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                  }
                }}
              />
            </div>
            <Input
              padding="6px"
              color="$main"
              placeholder="Enter link URL"
              backgroundColor="$background"
              margin="0px"
              pl="4px"
              minWidth="250px"
              textStyle="brand.body"
              fontWeight="bold"
              ref={inputRef}
              value={editedLinkUrl}
              onChange={(event) => {
                setEditedLinkUrl(event.target.value);
              }}
              onKeyDown={(event) => {
                monitorInputInteraction(event);
              }}
            />
            <div className="flex flex-row justify-end gap-[6px]">
              <Button
                color="brand.main"
                padding="0px"
                variant="link"
                aria-label="link-cancel"
                border="none"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setIsLinkEditMode(false);
                }}
              >
                Cancel
              </Button>
              <Button
                color="brand.main"
                padding="0px"
                variant="link"
                border="none"
                aria-label="link-confirm"
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleLinkSubmission}
              >
                Confirm
              </Button>
            </div>
          </div>
        ) : (
          <div className={popoverClassname}>
            <div className="flex justify-between items-center">
              <p className="typography-action-sm">Links</p>
              <IconButton
                variant="icon"
                icon={<RiCloseFill />}
                color="brand.main"
                height="fit-content"
                aria-label="Format text as italics"
                className="text-main"
                onClick={() => {
                  setIsLink(false);
                  setIsLinkEditMode(false);
                  if (editedLinkUrl === '') {
                    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                  }
                }}
              />
            </div>
            <a
              href={sanitizeUrl(linkUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="typography-basic"
            >
              {linkUrl || 'BLANK'}
            </a>
            <div className="flex flex-row justify-end gap-[6px]">
              <Button
                color="brand.main"
                padding="0px"
                variant="link"
                aria-label="link-edit"
                border="none"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setEditedLinkUrl(linkUrl);
                  setIsLinkEditMode(true);
                }}
              >
                Edit
              </Button>
              <Button
                color="brand.main"
                padding="0px"
                variant="link"
                aria-label="link-trash"
                border="none"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function useFloatingLinkEditorToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  isLinkEditMode: boolean,
  setIsLinkEditMode: Dispatch<boolean>,
): JSX.Element | null {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);

  useEffect(() => {
    function updateToolbar() {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection);
        const linkParent = $findMatchingParent(node, $isLinkNode);
        const autoLinkParent = $findMatchingParent(node, $isAutoLinkNode);
        // We don't want this menu to open for auto links.
        if (linkParent !== null && autoLinkParent === null) {
          setIsLink(true);
        } else {
          setIsLink(false);
          if (linkParent?.getURL() === '') {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
          }
        }
      }
    }
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        (payload) => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            const linkNode = $findMatchingParent(node, $isLinkNode);
            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              window.open(linkNode.getURL(), '_blank');
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor]);

  return createPortal(
    <FloatingLinkEditor
      editor={activeEditor}
      isLink={isLink}
      anchorElem={anchorElem}
      setIsLink={setIsLink}
      isLinkEditMode={isLinkEditMode}
      setIsLinkEditMode={setIsLinkEditMode}
    />,
    anchorElem,
  );
}

export default function FloatingLinkEditorPlugin({
  anchorElem = document.body,
  isLinkEditMode,
  setIsLinkEditMode,
}: {
  anchorElem?: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingLinkEditorToolbar(editor, anchorElem, isLinkEditMode, setIsLinkEditMode);
}
